// MIRRORS: panel/components/produceIcon/produceIcon.tsx — identical copy; edit both, or delete this line on both to declare a deliberate split.
import type { ProduceIconSource } from './produceArt';
import styles from './produceIcon.module.scss';

export interface ProduceIconProps {
  icon: ProduceIconSource;
  /** Sizing from the calling surface; the default is one line of text. */
  className?: string;
}

/**
 * A product's or category's icon: the design team's art where it exists, the
 * emoji where it does not yet.
 *
 * The art's box is the pin's disc, square, and the art is placed inside it at
 * the designers' own offset and size (see produceArt.ts) — so an apple here is
 * the same apple, in the same place, as on its map pin. Only the pin draws the
 * disc itself; everywhere else the art sits on the page.
 *
 * Decorative: every surface names the product in text beside it.
 */
export function ProduceIcon({ icon, className }: ProduceIconProps) {
  const { art, emoji } = icon;

  // An emoji stays a plain glyph, exactly as it rendered before the art
  // existed — rows without art must not shift while the set fills in.
  if (!art) {
    return (
      <span className={`${styles.glyph} ${className ?? ''}`} aria-hidden>
        {emoji}
      </span>
    );
  }

  return (
    <span className={`${styles.box} ${className ?? ''}`} aria-hidden>
      <img
        className={styles.art}
        src={art.url}
        alt=""
        decoding="async"
        draggable={false}
        style={{
          left: `${art.x * 100}%`,
          top: `${art.y * 100}%`,
          width: `${art.w * 100}%`,
          height: `${art.h * 100}%`,
        }}
      />
    </span>
  );
}
